import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { MessageConfig, Theme } from '../types/theme';
import { ThemeLoader } from '../utils/themeLoader';
import { ClientIdentifier } from '../utils/clientIdentifier';

interface ThemeContextType {
  theme: Theme;
  messages: MessageConfig;
  clientId: string;
  isLoading: boolean;
  error: string | null;
  refreshTheme: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  fallbackClientId?: string;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ 
  children, 
  fallbackClientId = 'default' 
}) => {
  const [theme, setTheme] = useState<Theme | null>(null);
  const [messages, setMessages] = useState<MessageConfig | null>(null);
  const [clientId, setClientId] = useState<string>(fallbackClientId);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadThemeAndMessages = async (targetClientId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const themeLoader = ThemeLoader.getInstance();
      
      // Load theme and messages in parallel
      const [themeConfig, messageConfig] = await Promise.all([
        themeLoader.loadTheme(targetClientId),
        themeLoader.loadMessages(targetClientId)
      ]);

      setTheme(themeConfig.theme);
      setMessages(messageConfig);
      setClientId(targetClientId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load theme';
      setError(errorMessage);
      console.error('Theme loading error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshTheme = async () => {
    const currentClientId = ClientIdentifier.getClientId();
    await loadThemeAndMessages(currentClientId);
  };

  useEffect(() => {
    const currentClientId = ClientIdentifier.getClientId();
    loadThemeAndMessages(currentClientId);
  }, []);

  // Apply CSS custom properties when theme changes
  useEffect(() => {
    if (theme) {
      applyThemeToCSS(theme);
    }
  }, [theme]);

  const value: ThemeContextType = {
    theme: theme || {} as Theme,
    messages: messages || {} as MessageConfig,
    clientId,
    isLoading,
    error,
    refreshTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * Hook to get just the theme (for components that don't need messages)
 */
export const useThemeOnly = (): Theme => {
  const { theme } = useTheme();
  return theme;
};

/**
 * Hook to get just the messages (for components that don't need theme styling)
 */
export const useMessages = (): MessageConfig => {
  const { messages } = useTheme();
  return messages;
};

/**
 * Apply theme properties as CSS custom properties
 */
function applyThemeToCSS(theme: Theme): void {
  const root = document.documentElement;
  
  // Apply colors
  Object.entries(theme.colors).forEach(([key, value]) => {
    root.style.setProperty(`--color-${key}`, value);
  });
  
  // Apply typography
  root.style.setProperty('--font-family', theme.typography.fontFamily);
  Object.entries(theme.typography.fontSize).forEach(([key, value]) => {
    root.style.setProperty(`--font-size-${key}`, value);
  });
  Object.entries(theme.typography.fontWeight).forEach(([key, value]) => {
    root.style.setProperty(`--font-weight-${key}`, value);
  });
  Object.entries(theme.typography.lineHeight).forEach(([key, value]) => {
    root.style.setProperty(`--line-height-${key}`, value);
  });
  
  // Apply spacing
  Object.entries(theme.spacing).forEach(([key, value]) => {
    root.style.setProperty(`--spacing-${key}`, value);
  });
  
  // Apply border radius
  Object.entries(theme.borderRadius).forEach(([key, value]) => {
    root.style.setProperty(`--border-radius-${key}`, value);
  });
  
  // Apply shadows
  Object.entries(theme.shadows).forEach(([key, value]) => {
    root.style.setProperty(`--shadow-${key}`, value);
  });
}

export default ThemeContext;
